/**
 * Pending-purchase marker.
 *
 * Records the RevenueCat external_id that a native purchase was made under,
 * so that after sign-in the server can CLAIM that purchase against the
 * signed-in Supabase user (see claim-native-purchase edge function).
 *
 * Rules:
 *   - V2Subscribe writes (with externalId) right before/after a native purchase
 *   - V2Auth + AppGate read it and trigger a server claim
 *   - This is NOT access truth; verify-entitlement remains authoritative
 *   - Persisted in localStorage so native force-close cannot wipe it
 *   - 30-minute TTL — stale markers are ignored
 *   - Never cleared on poll timeout; only on success or explicit "Start over"
 */

const KEY = 'pendingPurchase';
const TTL_MS = 30 * 60 * 1000;

const stores = (): Storage[] => {
  const list: Storage[] = [];
  try { list.push(localStorage); } catch { /* no-op */ }
  try { list.push(sessionStorage); } catch { /* no-op */ }
  return list;
};

export type PendingPurchaseStatus = 'purchased' | 'claiming' | 'claimed' | 'failed';
export type PendingPurchaseKind = 'device' | 'user';

export type PendingPurchase = {
  provider: 'revenuecat';
  platform: 'ios';
  externalId: string;
  createdAt: number;
  status?: PendingPurchaseStatus;
  /**
   * 'device' — purchase made signed-out under device_xxx; needs server claim.
   * 'user'   — purchase made signed-in under user.id; only needs restore+verify.
   * Legacy markers without `kind` are treated as 'device'.
   */
  kind?: PendingPurchaseKind;
};

export function writePendingPurchase(
  externalId: string,
  kind: PendingPurchaseKind = 'device',
): void {
  if (!externalId || typeof externalId !== 'string') {
    console.warn('[pending-purchase] refusing to write without externalId');
    return;
  }
  try {
    const payload: PendingPurchase = {
      provider: 'revenuecat',
      platform: 'ios',
      externalId,
      createdAt: Date.now(),
      status: 'purchased',
      kind,
    };
    const serialized = JSON.stringify(payload);
    stores().forEach((s) => s.setItem(KEY, serialized));
    console.log('[purchase-flow] pending_purchase_written', { externalId, kind });
  } catch {
    /* no-op */
  }
}

export function readPendingPurchase(): PendingPurchase | null {
  try {
    const raw = stores().map((s) => s.getItem(KEY)).find(Boolean);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPurchase;
    if (
      parsed?.provider !== 'revenuecat' ||
      typeof parsed.createdAt !== 'number' ||
      typeof parsed.externalId !== 'string' ||
      !parsed.externalId
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function markPendingPurchase(status: PendingPurchaseStatus): void {
  const current = readPendingPurchase();
  if (!current) return;
  try {
    const next: PendingPurchase = { ...current, status };
    const serialized = JSON.stringify(next);
    stores().forEach((s) => s.setItem(KEY, serialized));
  } catch {
    /* no-op */
  }
}

export function clearPendingPurchase(): void {
  try {
    stores().forEach((s) => s.removeItem(KEY));
    console.log('[purchase-flow] pending_purchase_cleared');
  } catch {
    /* no-op */
  }
}

export function isPendingPurchaseFresh(p: PendingPurchase | null): boolean {
  return !!p && Date.now() - p.createdAt < TTL_MS;
}
